import { Box, Grid, Typography, Paper, Divider, Chip, CircularProgress } from "@mui/material";
import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getOrderById } from "../../../Redux/Customers/Order/Action";
import OrderTraker from "./OrderTraker";
import AddressCard from "../adreess/AdreessCard";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ReceiptIcon from "@mui/icons-material/Receipt";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import { getBoxSizeDisplay } from "../../../utils/boxPricing";


/**
 * Status to Tracker Step mapping
 */
const TRACKER_STEPS = {
  PENDING: 0,
  PLACED: 1,
  CONFIRMED: 2,
  SHIPPED: 3,
  DELIVERED: 4,
};

const OrderDetails = () => {
  const { orderId } = useParams();
  const dispatch = useDispatch();
  const { order } = useSelector((store) => store);

  useEffect(() => {
    dispatch(getOrderById(orderId));
  }, [orderId, dispatch]);

  if (order.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  const orderData = order.order;
  if (!orderData) return null;

  return (
    <Box className="px-5 lg:px-20 py-10 space-y-8">
      {/* Header */}
      <Box className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Order Details
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Order #{orderData.id} • {new Date(orderData.createdAt).toLocaleDateString("en-US", { month: 'short', day: 'numeric', year: 'numeric' })}
          </Typography>
        </Box>
        <Chip
          label={orderData.orderStatus}
          color={orderData.orderStatus === 'DELIVERED' ? "success" : "primary"}
          variant="filled"
          sx={{ fontWeight: 'bold', px: 2 }}
        />
      </Box>

      {/* Address and Tracking */}
      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Box className="flex items-center gap-2 mb-4">
              <LocalShippingIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">Delivery Address</Typography>
            </Box>
            <AddressCard address={orderData.shippingAddress} />
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Box className="flex items-center gap-2 mb-6">
              <ShoppingBagIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">Order Status Tracker</Typography>
            </Box>
            <Box sx={{ py: 2 }}>
              <OrderTraker activeStep={TRACKER_STEPS[orderData.orderStatus] || 0} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Special Instructions */}
      {orderData.specialInstructions && (
        <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 3, bgcolor: "amber.50", borderColor: "amber.200" }}>
          <Box className="flex items-center gap-2 mb-2">
            <NoteAltIcon sx={{ color: "amber.700" }} />
            <Typography variant="h6" fontWeight="bold" sx={{ color: "amber.900" }}>Special Instructions</Typography>
          </Box>
          <Typography variant="body1" sx={{ color: "amber.900", fontStyle: "italic" }}>
            "{orderData.specialInstructions}"
          </Typography>
        </Paper>
      )}

      {/* Order Items and Price Summary */}

      <Grid container spacing={4}>
        {/* Items List */}
        <Grid item xs={12} lg={8}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Order Items</Typography>
          <Box className="space-y-4">
            {orderData.orderItems?.map((item) => (
              <Paper
                key={item.id}
                elevation={0}
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 3,
                  transition: 'background-color 0.2s',
                  '&:hover': { bgcolor: 'grey.50' }
                }}
              >
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={4} sm={2}>
                    <img
                      className="w-full aspect-square object-cover rounded-lg border"
                      src={item.product?.imageUrl}
                      alt={item.product?.title}
                    />
                  </Grid>
                  <Grid item xs={8} sm={10}>
                    <Box className="flex flex-col sm:flex-row justify-between items-start gap-2">
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {item.product?.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {getBoxSizeDisplay(item.size)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Size: <Box component="span" color="text.primary" fontWeight="500">{getBoxSizeDisplay(item.size)}</Box>
                          {" | "}
                          Quantity: <Box component="span" color="text.primary" fontWeight="500">{item.quantity}</Box>
                        </Typography>
                      </Box>
                      <Typography variant="h6" fontWeight="bold" color="primary.main">
                        ${item.discountedPrice?.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>
        </Grid>

        {/* Price Summary */}
        <Grid item xs={12} lg={4}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Order Summary</Typography>
          <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 3, position: 'sticky', top: '20px' }}>
            <Box className="flex items-center gap-2 mb-4">
              <ReceiptIcon color="primary" />
              <Typography variant="subtitle1" fontWeight="bold">Payment Details</Typography>
            </Box>

            <Box className="space-y-3">
              <Box className="flex justify-between">
                <Typography color="text.secondary">Items Total ({orderData.totalItem} items)</Typography>
                <Typography>${orderData.totalPrice?.toFixed(2)}</Typography>
              </Box>
              <Box className="flex justify-between">
                <Typography color="text.secondary">Discount</Typography>
                <Typography color="success.main">-${orderData.discount?.toFixed(2)}</Typography>
              </Box>
              <Box className="flex justify-between">
                <Typography color="text.secondary">Delivery Charges</Typography>
                <Typography sx={{ fontStyle: "italic", color: "primary.main" }}>Calculated at the Time of Delivery</Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box className="flex justify-between items-center">
                <Typography variant="h6" fontWeight="bold">Total Amount</Typography>
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  ${orderData.totalDiscountedPrice?.toFixed(2)}
                </Typography>
              </Box>
            </Box>


          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderDetails;
